import { CompleteState } from '@/frontend/SVGs/CompleteState';
import { ErrorState } from '@/frontend/SVGs/ErrorState';
import { I18n } from '@/i18n/I18n';
import { RUN_STATE } from '@/types/commandingRunTaskTypes';
import { useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { CanceledState } from '@/frontend/SVGs/CanceledState';
import { PendingState } from '@/frontend/SVGs/PendingState';
import { Task } from './CommandingRunBody';
import { useGetAllInputParametersForOperation } from '@/frontend/hooks/parameters';
import { Operation } from '@/backend/models/operation';
import { REGEX } from '@/backend/lib/regex';
import { CollapseButton } from '../buttons/CollapseButton';

export function TaskRow({ task, operation }: { task: Task, operation: Operation }) {
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [showFields, setShowFields] = useState(task.state !== RUN_STATE.COMPLETED && task.state !== RUN_STATE.ERROR);
  const parameters = useGetAllInputParametersForOperation(operation);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const diff = task.startTime ? Math.floor((now - new Date(task.startTime).getTime()) / 1000) : 0;
      setElapsedTime(diff);
    }, 100);

    return () => clearInterval(interval);
  }, [task.startTime]);

  const parsedFields = useMemo(() => task.fields?.split(/(<<[^>>]+>>)/).map((part) => {
    if (part.match(REGEX.PARAMETER_IDENTIFIER)) {
      const foundInput = parameters.find(param => param.identifier === part.replace(/<|>/g, ''));
      if (foundInput) {
        return foundInput.getFields()[0]?.getValue();
      }
    }
    return part;
  }).join(''), [task.fields, parameters]);

  return (
    <div>
      <div className='d-flex gap-1 align-items-center'>
        { parsedFields
          ? <CollapseButton showElement={showFields} setShowElement={setShowFields} title={task.alias} size='sm' />
          : <div style={{ width: 24, height: 24 }} />
        }
        <StateIcon state={task.state} tooltipText={task.tooltip}/>
        <small aria-label='task-alias'>{task.alias}</small>
        { task.state === RUN_STATE.RUNNING && <small aria-label={I18n.get('runningDuration')}>{formatTime(elapsedTime)}</small> }
      </div>
      { showFields && parsedFields && <small className='text-muted ps-4' style={{ whiteSpace: 'pre-wrap', display: 'block' }}>{ parsedFields }</small> }
    </div>
  );
}


export function StateIcon(props: { state?: RUN_STATE, tooltipText?: string }) {
  const { state = RUN_STATE.EMPTY, tooltipText } = props;

  if (state === RUN_STATE.RUNNING) return (
    <OverlayTrigger
    placement="top"
    overlay={
      <Popover>
        <Popover.Header as="h3">{I18n.get('running')}</Popover.Header>
        <Popover.Body>
          {tooltipText}
        </Popover.Body>
    </Popover>}
  >
    <div className="spinner-border text-info d-flex" role="status" style={{height:'16px', width: '16px'}}>
      <span className="sr-only" aria-label={I18n.get('running')} />
    </div>
  </OverlayTrigger>
  );

  if (state === RUN_STATE.CANCELLED) return (
    <OverlayTrigger
    placement="top"
    overlay={
      <Popover>
        <Popover.Header as="h3" className=''>{I18n.get('cancelled')}</Popover.Header>
        <Popover.Body>
          {tooltipText}
        </Popover.Body>
      </Popover>
    }
    >
      <div className='d-flex align-items-center'>
        <CanceledState />
      </div>
    </OverlayTrigger>
  );

  if (state === RUN_STATE.COMPLETED) return (
    <OverlayTrigger
    placement="top"
    overlay={
      <Popover>
        <Popover.Header as="h3" >{I18n.get('completed')}</Popover.Header>
        <Popover.Body>
          {tooltipText}
        </Popover.Body>
      </Popover>}
     >

      <div className='d-flex align-items-center'>
        <CompleteState />
      </div>
    </OverlayTrigger>
  );
  if (state === RUN_STATE.ERROR) return (
    <OverlayTrigger
    placement="top"
    overlay={
      <Popover>
        <Popover.Header as="h3" className='text-danger-emphasis bg-danger-subtle'>{I18n.get('errored')}</Popover.Header>
        <Popover.Body>
          {tooltipText}
        </Popover.Body>
      </Popover>}
    >
      <div className='d-flex align-items-center'>
        <ErrorState />
      </div>
    </OverlayTrigger>
  );

  return (
    <OverlayTrigger placement="top" overlay={
      <Popover>
        <Popover.Header as="h3">{I18n.get('pending')}</Popover.Header>
        <Popover.Body>
          {tooltipText}
        </Popover.Body>
      </Popover>}>
      <div className='d-flex align-items-center'>
        <PendingState />
      </div>
  </OverlayTrigger>
  );
}
